import { uiText } from '@/i18n/interface';

import { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { PaymentCategory } from '@/types/payment';
import { useEffectiveDojo } from './useEffectiveDojo';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  payment_status?: string;
  is_active?: boolean;
}

interface StudentPayment {
  id: string;
  studentId: string;
  studentName: string;
  email: string;
  category: string;
  subscriptionType: '1year' | '6months' | '3months' | 'monthly';
  paymentPlan: '1' | '2' | '3';
  totalAmount: number;
  amountPaid: number;
  installmentsPaid: number;
  totalInstallments: number;
  status: 'pending' | 'partial' | 'complete';
  nextDueDate?: string;
  startDate?: string;
  endDate?: string;
  paymentDate?: string;
}

export const usePaymentData = () => {
  const [categories, setCategories] = useState<PaymentCategory[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [studentPayments, setStudentPayments] = useState<StudentPayment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { effectiveDojoId, shouldFilterByDojo } = useEffectiveDojo();

  const fetchCategories = async () => {
    try {
      let query = supabase
        .from('payment_categories')
        .select('*')
        .order('name');

      // Filter by dojo if we should (not super_user or in impersonation mode)
      if (shouldFilterByDojo && effectiveDojoId) {
        query = query.eq('dojo_id', effectiveDojoId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }

      const formattedCategories = data?.map(cat => ({
        id: cat.id,
        name: cat.name,
        amounts: {
          // Convert from cents to decimal for display
          oneYear: (cat.one_year_amount || 0) / 100,
          sixMonths: (cat.six_months_amount || 0) / 100,
          threeMonths: (cat.three_months_amount || 0) / 100,
          monthly: (cat.monthly_amount || 0) / 100
        },
        currency: cat.currency || 'USD',
        paymentMethods: Array.isArray(cat.payment_methods)
          ? cat.payment_methods as string[]
          : ['bank_transfer'],
        isActive: cat.is_active !== false,
        stripeIntegrationEnabled: cat.stripe_integration_enabled || false,
        stripePriceIds: (cat.stripe_price_ids && typeof cat.stripe_price_ids === 'object' && !Array.isArray(cat.stripe_price_ids))
          ? cat.stripe_price_ids as Record<string, string>
          : {},
        paymentLinks: (cat.payment_links && typeof cat.payment_links === 'object' && !Array.isArray(cat.payment_links))
          ? cat.payment_links as Record<string, string>
          : {},
        autoGenerateLinks: cat.auto_generate_links || false
      })) || [];

      setCategories(formattedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStudents = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('profiles')
        .select('*')
        .eq('role', 'student')
        .order('first_name');

      // Filter by dojo if we should (not super_user or in impersonation mode)
      if (shouldFilterByDojo && effectiveDojoId) {
        query = query.eq('dojo_id', effectiveDojoId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching students:', error);
        toast({
          title: uiText("Error"),
          description: uiText("Failed to load students"),
          variant: "destructive"
        });
        return;
      }

      setStudents(data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to load students"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudentPayments = async () => {
    try {
      console.log('📊 Fetching student payments for dojo:', effectiveDojoId);

      let query = supabase
        .from('payment_instructions')
        .select(`
          *,
          profiles!inner(first_name, last_name, email)
        `)
        .order('created_at', { ascending: false });

      // Filter by dojo if we should (not super_user or in impersonation mode)
      if (shouldFilterByDojo && effectiveDojoId) {
        query = query.eq('dojo_id', effectiveDojoId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Error fetching student payments:', error);
        toast({
          title: uiText("Error"),
          description: uiText("Failed to load payment data"),
          variant: "destructive"
        });
        return;
      }

      console.log('✅ Raw payment data:', data);

      const formattedPayments = data?.map(payment => ({
        id: payment.id,
        studentId: payment.student_id,
        studentName: `${payment.profiles.first_name} ${payment.profiles.last_name}`,
        email: payment.profiles.email,
        category: payment.category_name || 'General',
        subscriptionType: payment.subscription_type as '1year' | '6months' | '3months' | 'monthly',
        paymentPlan: payment.payment_plan as '1' | '2' | '3',
        totalAmount: payment.total_amount || 0, // Keep in cents for database consistency
        amountPaid: payment.amount_paid || 0, // Keep in cents for database consistency
        installmentsPaid: payment.installments_paid || 0,
        totalInstallments: payment.total_installments || 1,
        status: payment.status as 'pending' | 'partial' | 'complete',
        nextDueDate: payment.next_due_date,
        startDate: payment.start_date,
        endDate: payment.end_date,
        paymentDate: payment.payment_date,
        dojoId: payment.dojo_id // Include dojo_id for invoice generation
      })) || [];

      console.log('📋 Formatted payments:', formattedPayments);
      setStudentPayments(formattedPayments);
    } catch (error) {
      console.error('❌ Error fetching student payments:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to load payment data"),
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (effectiveDojoId || !shouldFilterByDojo) {
      fetchCategories();
      fetchStudents();
      fetchStudentPayments();
    }
  }, [effectiveDojoId, shouldFilterByDojo]);

  const refreshCategories = async () => {
    await fetchCategories();
  };

  return {
    categories,
    students,
    studentPayments,
    isLoading,
    setCategories,
    fetchCategories,
    fetchStudents,
    fetchStudentPayments,
    refreshCategories
  };
};
